import { useCallback, useMemo, useState } from 'react';
import { Layers, Plus } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useSession } from '@/stores/session';
import { priorityLabel, type Priority } from '../constants';
import { workItemRef } from '../paths';
import { useSelectedWorkItem } from '../useSelectedWorkItem';
import { AssigneeAvatars } from './AssigneeAvatars';
import {
  modulesHooks,
  statesHooks,
  useCreateWorkItem,
  useMoveWorkItem,
  useWorkItems,
} from '../queries';
import type { Module, Project, State, WorkItem } from '../types';
import { filterParams, type WorkItemFilters } from './workItemFilters';
import { DEFAULT_ORDER, sortWorkItems, type WorkItemOrder } from './workItemOrder';
import { WorkItemDetailDrawer } from './WorkItemDetailDrawer';
import { WorkItemTypeIcon } from './WorkItemTypeIcon';

const PRIORITY_DOT: Record<Priority, string> = {
  urgent: 'bg-red-500',
  high: 'bg-orange-500',
  medium: 'bg-amber-400',
  low: 'bg-sky-400',
  none: 'bg-muted-foreground/30',
};

interface BoardProps {
  project: Project;
  filters?: WorkItemFilters;
  order?: WorkItemOrder;
}

export function WorkItemsBoard({ project, filters, order = DEFAULT_ORDER }: BoardProps) {
  // Viewers are read-only; member/lead guests + staff may add / drag cards.
  const isGuest = useSession((s) => s.role) === 'guest';
  const canWrite = !isGuest || project.my_role === 'member' || project.my_role === 'lead';
  const statesQuery = statesHooks.useList({ project: project.id });
  // Modules is a staff-only endpoint — skip the fetch for guests (they'd 403);
  // the card chip simply won't render for them.
  const modulesQuery = modulesHooks.useList({ project: project.id }, !isGuest);
  const itemsQuery = useWorkItems({ project: project.id, page_size: 200, ...filterParams(filters) });
  const createItem = useCreateWorkItem();
  const moveItem = useMoveWorkItem();
  const [selected, setSelected] = useSelectedWorkItem();
  const [dragId, setDragId] = useState<number | null>(null);

  const states = useMemo(() => {
    const raw = statesQuery.data;
    const list = (Array.isArray(raw) ? raw : (raw?.results ?? [])) as State[];
    return [...list].sort((a, b) => a.order - b.order);
  }, [statesQuery.data]);

  // Work items only carry `module` (id); resolve names for the card chip.
  const moduleNameById = useMemo(() => {
    const raw = modulesQuery.data;
    const list = (Array.isArray(raw) ? raw : (raw?.results ?? [])) as Module[];
    return new Map(list.map((m) => [m.id, m.name]));
  }, [modulesQuery.data]);

  const itemsByState = useMemo(() => {
    const map = new Map<number, WorkItem[]>();
    // Sort the fetched page client-side per the chosen order, then bucket — so
    // each column shows its items in that order (manual = server order).
    for (const item of sortWorkItems(itemsQuery.data?.results ?? [], order)) {
      if (item.state == null) continue;
      const bucket = map.get(item.state) ?? [];
      bucket.push(item);
      map.set(item.state, bucket);
    }
    return map;
  }, [itemsQuery.data, order]);

  if (statesQuery.isPending || itemsQuery.isPending) {
    return (
      <div className="flex gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-96 w-72 shrink-0" />
        ))}
      </div>
    );
  }

  function handleDrop(stateId: number) {
    if (dragId == null) return;
    const item = itemsQuery.data?.results.find((i) => i.id === dragId);
    setDragId(null);
    if (!item || item.state === stateId) return;
    moveItem.mutate({ id: item.public_id, body: { state: stateId } });
  }

  return (
    <>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {states.map((state) => {
          const items = itemsByState.get(state.id) ?? [];
          return (
            <div
              key={state.id}
              className="bg-muted/40 flex min-h-32 w-72 shrink-0 flex-col rounded-lg pb-2"
              onDragOver={(e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              }}
              onDrop={() => handleDrop(state.id)}
            >
              <div className="flex items-center gap-2 px-3 py-2.5">
                <span
                  className="size-2.5 rounded-full"
                  style={{ backgroundColor: state.color || '#94a3b8' }}
                />
                <span className="text-sm font-medium">{state.name}</span>
                <span className="text-muted-foreground text-xs">{items.length}</span>
              </div>

              <div className="space-y-2 px-2 pb-1">
                {items.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    draggable={canWrite}
                    onDragStart={(e) => {
                      if (!canWrite) return;
                      setDragId(item.id);
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onClick={() => setSelected(workItemRef(item))}
                    className={cn(
                      'bg-background w-full rounded-md border p-2.5 text-left shadow-sm',
                      'hover:border-primary/40',
                      canWrite && 'cursor-grab active:cursor-grabbing',
                    )}
                  >
                    <p className="text-sm break-words">{item.title}</p>
                    <div className="mt-2 flex items-center gap-2">
                      <WorkItemTypeIcon type={item.type_detail} className="size-3.5 shrink-0" />
                      <span className="text-muted-foreground shrink-0 font-mono text-[11px] whitespace-nowrap">
                        {item.identifier}
                      </span>
                      {item.priority !== 'none' ? (
                        <span className="flex shrink-0 items-center gap-1">
                          <span className={cn('size-2 rounded-full', PRIORITY_DOT[item.priority])} />
                          <span className="text-muted-foreground text-[11px]">
                            {priorityLabel(item.priority)}
                          </span>
                        </span>
                      ) : null}
                      {item.sub_item_count > 0 ? (
                        <Badge variant="outline" className="shrink-0 text-[10px]">
                          {item.sub_item_count} sub
                        </Badge>
                      ) : null}
                      <span className="ml-auto shrink-0">
                        <AssigneeAvatars assignees={item.assignee_details} />
                      </span>
                    </div>
                    {/* Own line so long module names wrap instead of truncating. */}
                    {item.module != null && moduleNameById.has(item.module) ? (
                      <div className="text-muted-foreground mt-1.5 flex items-start gap-1 text-[11px]">
                        <Layers className="mt-px size-3 shrink-0" />
                        <span className="min-w-0 break-words">{moduleNameById.get(item.module)}</span>
                      </div>
                    ) : null}
                  </button>
                ))}
              </div>

              {canWrite ? (
                <QuickAdd
                  identifier={project.identifier}
                  pending={createItem.isPending}
                  onAdd={(title) =>
                    createItem.mutate({ project: project.id, title, state: state.id })
                  }
                />
              ) : null}
            </div>
          );
        })}
      </div>

      <WorkItemDetailDrawer
        project={project}
        workItemId={selected}
        onClose={() => setSelected(null)}
      />
    </>
  );
}

/** Inline "New work item" — Enter saves and keeps the field open for the next. */
function QuickAdd({
  identifier,
  pending,
  onAdd,
}: {
  identifier: string;
  pending: boolean;
  onAdd: (title: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  // Focus the field when it opens (a11y-friendly alternative to autoFocus).
  const focusOnMount = useCallback((el: HTMLInputElement | null) => el?.focus(), []);

  if (!open) {
    return (
      <div className="px-2">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-muted-foreground hover:text-foreground hover:border-foreground/30 flex w-full items-center gap-1.5 rounded-md border border-dashed px-3 py-2 text-sm transition-colors"
        >
          <Plus className="size-4" /> New work item
        </button>
      </div>
    );
  }

  return (
    <div className="p-2">
      <div className="bg-background rounded-md border p-2 shadow-sm">
        <p className="text-muted-foreground font-mono text-[11px]">{identifier}</p>
        <Input
          ref={focusOnMount}
          value={title}
          disabled={pending}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && title.trim()) {
              onAdd(title.trim());
              setTitle('');
            } else if (e.key === 'Escape') {
              setOpen(false);
              setTitle('');
            }
          }}
          onBlur={() => {
            if (!title.trim()) setOpen(false);
          }}
          placeholder="Work item title"
          className="mt-1 h-8 border-0 px-0 shadow-none focus-visible:ring-0"
        />
      </div>
      <p className="text-muted-foreground mt-1 px-1 text-[11px] italic">
        Press Enter to add another work item
      </p>
    </div>
  );
}
